/**
 * Order-total recompute — the bridge between the pure engine and the `order` /
 * `order_item` shape in `@repo/db`. **Owned by P5, consumed by P6.**
 *
 * At checkout the client-submitted prices are never trusted: P6 rebuilds each
 * line from the catalogue, calls {@link quoteOrder}, and freezes the returned
 * figures onto the order. Every column this fills has a database CHECK behind it
 * (`order_totals_nonneg`, `order_gross_covers_net`, `order_item_price_quote_xor`,
 * `order_item_priced_has_total`…), so the shapes here are chosen to satisfy them
 * by construction.
 *
 * ── WHAT A LINE COSTS IS NOW ONE MULTIPLICATION ──────────────────────────
 * `unitPriceNetMinor × quantity`, plus any extra passthrough. That is the whole
 * rule. This function used to price each basket TWICE — once with the volume
 * rungs and no basket bonus, once at list price with the bonus — and charge the
 * cheaper of the two, because spec §14 forbade the two from stacking.
 *
 * All of it is gone. The owner's decision: **the only discount is a code an
 * admin issues.** That code is applied once, later, in `orders.service.ts`,
 * against the total this returns — so it is the single place in the codebase
 * where a customer's price can move away from the catalogue's.
 *
 * `discountNetMinor` therefore always leaves here as 0. It is still on the
 * shape because the caller adds the code's amount to it before freezing the
 * header, and a field that exists in the database is clearer kept than
 * reconstructed.
 *
 * Still PURE: catalogue-resolved numbers in, numbers out. No DB handle.
 */
import { DEFAULT_PRICING_POLICY, DEFAULT_TAX_RATE_BPS, type PricingPolicy } from './constants.ts'
import { type CartLine, cartTotals, grossMinor } from './pricing-engine.ts'

/**
 * One line as the caller resolves it from the catalogue (price, tax, quantity).
 * A quote-only line sets `unitPriceNetMinor: null` and `isQuoteOnly: true` — the
 * pair the schema enforces.
 *
 * Four fields left this shape when the ladders did: `isVolumeEligible`,
 * `serviceKey`, `countsTowardMultiService` and `volumeSteps`. Every one of them
 * existed to answer "which rung does this line earn", and nothing earns a rung
 * any more.
 */
export interface OrderLineInput {
  readonly unitPriceNetMinor: number | null
  readonly isQuoteOnly: boolean
  readonly taxRateBps: number
  readonly quantity: number
  /**
   * Booked by the day — a hire.
   *
   * Kept because the ORDER records what it sold, not because it changes a
   * price: spec §11 · I4 sells five fixed windows, so the variant's price is
   * already the price of the whole hire.
   */
  readonly isRental: boolean
  /** How long the window is, for the record. Never a multiplier. */
  readonly rentalDays?: number
  /**
   * NET grosze on this line that are not `quantity × price` — today, spec §8's
   * raw-footage overage (G7).
   *
   * A separate field rather than folded into the unit price because it is a
   * passthrough for time somebody has to spend watching tape. It is added after
   * the line total and is not something a code's percentage was ever meant to
   * reward.
   */
  readonly extraNetMinor?: number
}

/** Per-line result, shaped to drop straight onto an `order_item` row. */
export interface OrderQuoteLine {
  readonly isQuoteOnly: boolean
  readonly taxRateBps: number
  readonly quantity: number
  /** Net grosze before extras; 0 for a quote-only line (which carries no price). */
  readonly baseNetMinor: number
  /**
   * Always 0.
   *
   * `order_item.discount_bps` is a real column carrying real history — orders
   * placed while the volume ladder was live are frozen with the rung they were
   * charged at, and those figures must never be rewritten. Nothing WRITES a
   * non-zero value here any more.
   */
  readonly discountBps: number
  /** `unitPrice × qty`, net grosze. NULL for a quote-only line. */
  readonly lineTotalNetMinor: number | null
  /**
   * Undiscounted extra on this line — the footage passthrough (G7).
   *
   * Reported SEPARATELY from `lineTotalNetMinor` so the arithmetic stays
   * readable. What gets FROZEN onto `order_item.line_total_net_minor` is their
   * sum — see `toOrderLine`.
   */
  readonly extraNetMinor: number
}

/** Header + lines, ready to persist. */
export interface OrderQuote {
  readonly lines: OrderQuoteLine[]
  /** Sum of the priced lines, extras included. */
  readonly subtotalNetMinor: number
  /** Always 0 from here; the caller adds a discount code's amount to it. */
  readonly discountNetMinor: number
  readonly totalNetMinor: number
  readonly totalGrossMinor: number
  /** Summary rate for the single-rate invoice; the lines carry the authoritative per-line rate. */
  readonly taxRateBps: number
  /** True when at least one line is quote-only — gates "do not charge" in P6/Ppay. */
  readonly hasQuoteOnlyItems: boolean
}

/** Reduce one input line to the total that is persisted against it. */
function quoteLine(line: OrderLineInput): OrderQuoteLine {
  if (line.isQuoteOnly || line.unitPriceNetMinor === null) {
    return {
      isQuoteOnly: true,
      taxRateBps: line.taxRateBps,
      quantity: Math.max(0, Math.trunc(line.quantity)),
      baseNetMinor: 0,
      discountBps: 0,
      lineTotalNetMinor: null,
      // A quote-only line carries no money at all, extras included: the CHECK
      // `order_item_quote_no_total` makes that a database rule, not a habit.
      extraNetMinor: 0,
    }
  }

  // Never rounded and never scaled by quantity: it is already a finished amount
  // by the time it gets here.
  const extra = Math.max(0, Math.trunc(line.extraNetMinor ?? 0))
  const quantity = Math.max(0, Math.trunc(line.quantity))
  const total = line.unitPriceNetMinor * quantity

  return {
    isQuoteOnly: false,
    taxRateBps: line.taxRateBps,
    quantity,
    baseNetMinor: total,
    discountBps: 0,
    lineTotalNetMinor: total,
    extraNetMinor: extra,
  }
}

/**
 * Recompute a basket into the numbers an `order` header and its `order_item`
 * rows freeze: every line at catalogue price, then the extras, then VAT.
 *
 * No rounding policy is applied to a line any more, and there is nothing left
 * for one to do: `unit × quantity` over integral grosze is already exact.
 * `policy` survives because {@link grossMinor} still rounds VAT with it.
 */
export function quoteOrder(
  input: readonly OrderLineInput[],
  policy: PricingPolicy = DEFAULT_PRICING_POLICY,
): OrderQuote {
  const lines = input.map(quoteLine)
  const cartLines: CartLine[] = lines.map((line) => ({
    lineTotalMinor: line.lineTotalNetMinor,
    isQuoteOnly: line.isQuoteOnly,
  }))
  const totals = cartTotals(cartLines)

  // Added on top of the lines, and never part of what a code could discount:
  // the extras are a passthrough for work somebody has to do.
  const extrasMinor = lines.reduce((acc, line) => acc + line.extraNetMinor, 0)

  // Header summary rate: the lines are authoritative. Uniform today (every
  // catalogue row is 23%); if a future mix appears, take the highest so the
  // single-rate gross never UNDER-states VAT (keeps `order_gross_covers_net`),
  // and let P6/Ppay move to per-line gross when a real mix exists.
  const pricedRates = lines.filter((l) => !l.isQuoteOnly).map((l) => l.taxRateBps)
  const taxRateBps = pricedRates.length === 0 ? DEFAULT_TAX_RATE_BPS : Math.max(...pricedRates)

  const totalNetMinor = totals.totalMinor + extrasMinor
  return {
    lines,
    // Includes the extras, which keeps the invariant every screen relies on:
    // the order's subtotal is the sum of what its lines were frozen at.
    subtotalNetMinor: totals.subtotalMinor + extrasMinor,
    discountNetMinor: 0,
    totalNetMinor,
    totalGrossMinor: grossMinor(totalNetMinor, taxRateBps, policy.rounding),
    taxRateBps,
    hasQuoteOnlyItems: lines.some((l) => l.isQuoteOnly),
  }
}
